import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { getErrorMessage } from '../lib/api';
import {
  collectAcceptedVersionIds,
  createAllowAllPreferences,
  createDefaultPreferences,
  fetchActivePolicies,
  fetchConsents,
  getRequiredVersionIds,
  isFullyConsented,
  mergePreferencesFromConsents,
  normalizeVersionId,
  resolvePdpaIdentity,
  revokeAllConsents,
  saveConsentsForVersions,
} from '../lib/pdpa';
import CookieSettingsModal from '../components/pdpa/CookieSettingsModal';
import PdpaConsentBanner from '../components/pdpa/PdpaConsentBanner';
import PolicyViewModal from '../components/pdpa/PolicyViewModal';

const PdpaContext = createContext(null);

const CONSENT_CHECK_DELAY_MS = 500;

export function PdpaProvider({ apiFetch, currentUser, isAuthenticated, logout, showToast, children }) {
  const [status, setStatus] = useState('idle');
  const [policyList, setPolicyList] = useState([]);
  const [requiredVersionIds, setRequiredVersionIds] = useState([]);
  const [acceptedVersionIds, setAcceptedVersionIds] = useState([]);
  const [preferences, setPreferences] = useState(createDefaultPreferences());
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settingsMode, setSettingsMode] = useState('consent');
  const [policyView, setPolicyView] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const savingRef = useRef(false);
  const autoOpenedRef = useRef(false);

  const identity = useMemo(() => resolvePdpaIdentity(currentUser), [currentUser]);
  const userType = identity?.userType || 'employee';

  const resetState = useCallback(() => {
    setStatus('idle');
    setPolicyList([]);
    setRequiredVersionIds([]);
    setAcceptedVersionIds([]);
    setPreferences(createDefaultPreferences());
    setSettingsOpen(false);
    setSettingsMode('consent');
    setPolicyView(null);
    setIsSaving(false);
  }, []);

  const refreshConsentState = useCallback(async () => {
    if (!identity?.empCode || !apiFetch) {
      resetState();
      return;
    }

    setStatus('loading_policies');
    try {
      const policies = await fetchActivePolicies(apiFetch);
      const requiredIds = getRequiredVersionIds(userType, policies);

      if (!requiredIds.length) {
        setPolicyList(policies);
        setRequiredVersionIds([]);
        setAcceptedVersionIds([]);
        setSettingsOpen(false);
        setStatus('no_policies');
        return;
      }

      setPolicyList(policies);
      setRequiredVersionIds(requiredIds);

      setStatus('checking_consent');
      const accepted = await fetchConsents(apiFetch, identity.empCode, userType);
      const acceptedIds = [...collectAcceptedVersionIds(accepted)];
      setAcceptedVersionIds(acceptedIds);

      const existing = isFullyConsented(requiredIds, accepted);
      setPreferences(mergePreferencesFromConsents(accepted, userType));
      setStatus(existing ? 'consented' : 'needs_consent');
    } catch (error) {
      setStatus('unavailable');
      showToast(getErrorMessage(error, 'โหลดข้อมูล PDPA ไม่สำเร็จ'));
    }
  }, [apiFetch, identity?.empCode, resetState, showToast, userType]);

  useEffect(() => {
    if (!isAuthenticated || !identity?.empCode) {
      resetState();
      return undefined;
    }

    const timer = setTimeout(() => {
      void refreshConsentState();
    }, CONSENT_CHECK_DELAY_MS);

    return () => clearTimeout(timer);
  }, [identity?.empCode, isAuthenticated, refreshConsentState, resetState]);

  useEffect(() => {
    if (status === 'needs_consent' && requiredVersionIds.length > 0 && !autoOpenedRef.current) {
      autoOpenedRef.current = true;
      setSettingsMode('consent');
      setSettingsOpen(true);
    }
    if (status !== 'needs_consent') {
      autoOpenedRef.current = false;
    }
  }, [requiredVersionIds.length, status]);

  useEffect(() => {
    if (status === 'no_policies') {
      setSettingsOpen(false);
    }
  }, [status]);

  const openSettings = useCallback((mode = 'edit') => {
    setSettingsMode(mode);
    setSettingsOpen(true);
  }, []);

  const closeSettings = useCallback(() => {
    setSettingsOpen(false);
  }, []);

  const openPolicyView = useCallback((versionId) => {
    const targetId = normalizeVersionId(versionId);
    const policy = policyList.find((item) => normalizeVersionId(item) === targetId);
    if (!policy?.ContentHtml) return;
    setPolicyView({
      versionId,
      title: policy.PolicyName,
      contentHtml: policy.ContentHtml,
    });
  }, [policyList]);

  const closePolicyView = useCallback(() => {
    setPolicyView(null);
  }, []);

  const persistPreferences = useCallback(async (nextPreferences, { revoke = false } = {}) => {
    if (!identity?.empCode || !requiredVersionIds.length || savingRef.current) return false;

    savingRef.current = true;
    setIsSaving(true);
    try {
      if (revoke) {
        await revokeAllConsents(apiFetch, {
          empCode: identity.empCode,
          userType,
          versionIds: requiredVersionIds,
        });
        await logout();
        return true;
      }

      await saveConsentsForVersions(apiFetch, {
        empCode: identity.empCode,
        userType,
        versionIds: requiredVersionIds,
        preferences: nextPreferences,
        acceptedVersionIds,
      });

      const accepted = await fetchConsents(apiFetch, identity.empCode, userType);
      const acceptedIds = [...collectAcceptedVersionIds(accepted)];
      const fullyConsented = isFullyConsented(requiredVersionIds, accepted);

      setAcceptedVersionIds(acceptedIds);
      setPreferences(nextPreferences);

      if (!fullyConsented) {
        setStatus('needs_consent');
        showToast('บันทึกไม่ครบทุกนโยบาย กรุณาลองอีกครั้ง');
        return false;
      }

      setStatus('consented');
      setSettingsOpen(false);
      showToast('บันทึกการตั้งค่า PDPA แล้ว');
      return true;
    } catch (error) {
      showToast(getErrorMessage(error, 'บันทึกความยินยอม PDPA ไม่สำเร็จ'));
      return false;
    } finally {
      savingRef.current = false;
      setIsSaving(false);
    }
  }, [
    acceptedVersionIds,
    apiFetch,
    identity?.empCode,
    logout,
    requiredVersionIds,
    showToast,
    userType,
  ]);

  const handleAllowAll = useCallback(() => {
    void persistPreferences(createAllowAllPreferences(userType));
  }, [persistPreferences, userType]);

  const handleRejectOptional = useCallback(() => {
    void persistPreferences(createDefaultPreferences(userType));
  }, [persistPreferences, userType]);

  const handleSaveSettings = useCallback((nextPreferences) => {
    void persistPreferences(nextPreferences);
  }, [persistPreferences]);

  const handleRevoke = useCallback(() => {
    void persistPreferences(preferences, { revoke: true });
  }, [persistPreferences, preferences]);

  const showBanner = status === 'needs_consent' && !settingsOpen;
  const canSubmit = requiredVersionIds.length > 0 && status !== 'loading_policies' && status !== 'checking_consent';
  const hasExistingConsent = status === 'consented';
  const pdpaCheckComplete = !['idle', 'loading_policies', 'checking_consent'].includes(status);
  const isPdpaEnabled = requiredVersionIds.length > 0;

  const value = useMemo(() => ({
    status,
    policyList,
    preferences,
    hasExistingConsent,
    acceptedVersionIds,
    isPdpaReady: isPdpaEnabled,
    isPdpaEnabled,
    pdpaCheckComplete,
    canAccessPdpa: Boolean(identity?.empCode),
    openSettings,
    refreshConsentState,
    handleAllowAll,
    isSaving,
  }), [
    acceptedVersionIds,
    handleAllowAll,
    hasExistingConsent,
    identity?.empCode,
    isPdpaEnabled,
    isSaving,
    openSettings,
    pdpaCheckComplete,
    policyList,
    preferences,
    refreshConsentState,
    requiredVersionIds.length,
    status,
  ]);

  return (
    <PdpaContext.Provider value={value}>
      {children}

      {showBanner && (
        <PdpaConsentBanner
          onOpenSettings={() => openSettings('consent')}
          onAllowAll={handleAllowAll}
          disabled={!canSubmit || isSaving}
          loading={isSaving}
        />
      )}

      {settingsOpen && isPdpaEnabled && (
        <CookieSettingsModal
          mode={settingsMode}
          userType={userType}
          policyList={policyList}
          preferences={preferences}
          isSaving={isSaving}
          canSubmit={canSubmit}
          onClose={closeSettings}
          onChangePreferences={setPreferences}
          onOpenPolicy={openPolicyView}
          onAllowAll={handleAllowAll}
          onRejectOptional={handleRejectOptional}
          onSave={handleSaveSettings}
          onRevoke={handleRevoke}
        />
      )}

      {policyView && (
        <PolicyViewModal
          title={policyView.title}
          contentHtml={policyView.contentHtml}
          onClose={closePolicyView}
        />
      )}
    </PdpaContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function usePdpa() {
  const ctx = useContext(PdpaContext);
  if (!ctx) throw new Error('usePdpa must be used within PdpaProvider');
  return ctx;
}
